import {
  updateRegistrantAccountLoginIdentityAccountsContact,
  updateRegistrantAccountUserAccountsContact,
  updateRegistrantAccountUserAdmin,
  updateRegistrantAccountUserCanMakePayments,
  updateRegistrantAccountUserInspectionAccess,
  updateRegistrantLoginIdentityInspectionAccess,
} from "@/hera-api/client";
import type { RegistrantAccountUserResponseSchema } from "@/hera-api/client/types.gen";
import { getQueryErrorMessage } from "@/lib/utils";

async function throwIfError(res: { error?: unknown }): Promise<void> {
  if (res.error) {
    throw new Error(getQueryErrorMessage(res.error));
  }
}

export async function saveRegistrantUserAccessChanges({
  original,
  draft,
}: {
  original: RegistrantAccountUserResponseSchema;
  draft: RegistrantAccountUserResponseSchema;
}): Promise<void> {
  if (Boolean(original.is_admin) !== Boolean(draft.is_admin)) {
    await throwIfError(
      await updateRegistrantAccountUserAdmin({
        path: { user_id: draft.id },
        body: { is_admin: Boolean(draft.is_admin) },
      }),
    );
  }

  if (
    Boolean(original.can_make_payments) !== Boolean(draft.can_make_payments)
  ) {
    await throwIfError(
      await updateRegistrantAccountUserCanMakePayments({
        path: { user_id: draft.id },
        body: { can_make_payments: Boolean(draft.can_make_payments) },
      }),
    );
  }

  const originalIdentities = original.login_identities ?? [];
  const draftIdentities = draft.login_identities ?? [];

  for (const draftLi of draftIdentities) {
    const originalLi = originalIdentities.find((li) => li.id === draftLi.id);
    if (!originalLi) {
      continue;
    }

    if (originalLi.is_accounts_contact !== draftLi.is_accounts_contact) {
      await throwIfError(
        await updateRegistrantAccountLoginIdentityAccountsContact({
          path: { login_identity_id: draftLi.id },
          body: { is_accounts_contact: draftLi.is_accounts_contact },
        }),
      );
    }

    const portalBody: {
      inspection_only?: boolean;
      license_access_mode?: "ALL" | "SELECTED";
    } = {};
    if (originalLi.inspection_only !== draftLi.inspection_only) {
      portalBody.inspection_only = draftLi.inspection_only;
    }
    if (originalLi.license_access_mode !== draftLi.license_access_mode) {
      portalBody.license_access_mode = draftLi.license_access_mode;
    }
    if (Object.keys(portalBody).length > 0) {
      await throwIfError(
        await updateRegistrantLoginIdentityInspectionAccess({
          path: { login_identity_id: draftLi.id },
          body: portalBody,
        }),
      );
    }
  }

  if (originalIdentities.length === 0) {
    if (
      Boolean(original.accounts_contact_before_login) !==
      Boolean(draft.accounts_contact_before_login)
    ) {
      await throwIfError(
        await updateRegistrantAccountUserAccountsContact({
          path: { user_id: draft.id },
          body: {
            is_accounts_contact: Boolean(draft.accounts_contact_before_login),
          },
        }),
      );
    }

    const stagedPortalBody: {
      inspection_only?: boolean;
      license_access_mode?: "ALL" | "SELECTED";
    } = {};
    if (
      original.inspection_only_before_login !==
      draft.inspection_only_before_login
    ) {
      stagedPortalBody.inspection_only = Boolean(
        draft.inspection_only_before_login,
      );
    }
    if (
      original.license_access_mode_before_login !==
      draft.license_access_mode_before_login
    ) {
      stagedPortalBody.license_access_mode =
        draft.license_access_mode_before_login ?? "SELECTED";
    }
    if (Object.keys(stagedPortalBody).length > 0) {
      await throwIfError(
        await updateRegistrantAccountUserInspectionAccess({
          path: { user_id: draft.id },
          body: stagedPortalBody,
        }),
      );
    }
  }
}
